import { type DocumentCirculation } from '@units/document/types/document.types.js';

/**
 * О каких документах спрашивают реестр: о тех, что в обращении, о выведенных из него, или обо всех.
 *
 * Перечень выписан кортежем, потому что это ещё и **порядок вариантов в поле отбора**: сперва
 * умолчание — рабочий набор, — затем выведенные, затем всё сразу. `satisfies` сверяет каждое
 * значение с контрактом, а `Record<DocumentCirculation, …>` ниже — полноту: состояние, добавленное
 * на сервере, становится ошибкой сборки здесь, а не пустым пунктом списка.
 */
export const DOCUMENT_CIRCULATIONS = [
  'IN_CIRCULATION',
  'WITHDRAWN',
  'ANY',
] as const satisfies readonly DocumentCirculation[];

/**
 * Умолчание — **смысл операции**, а не удобство.
 *
 * `GET /documents` без параметра отвечает «что у нас в работе», и выведенный документ из этого
 * ответа исключён — иначе вывод из обращения не значил бы ничего. Контраст с `externalProcessing`
 * намеренный: там отсутствие параметра означает отказ фильтровать, здесь оно **и есть** вопрос.
 *
 * Умолчание названо здесь, а не только в схеме адреса: его читают и схема, и фишки отбора, и
 * сброс, и два ответа на «что значит пустой адрес» разошлись бы молча.
 */
export const DEFAULT_DOCUMENT_CIRCULATION: DocumentCirculation = 'IN_CIRCULATION';

/**
 * Ключи, а не подписи (`rules/i18n.mdc` §5): текст берётся `t()` в компоненте.
 *
 * Выписаны поштучно, а не собраны как `documents.filters.circulation.${value}`: ключ, собранный в
 * рантайме, не виден гейту паритета словарей, и пропущенный перевод доезжает до юриста строкой
 * `documents.filters.circulation.WITHDRAWN` вместо «только выведенные» (ADR-0019).
 */
export const DOCUMENT_CIRCULATION_LABEL: Readonly<Record<DocumentCirculation, string>> = {
  IN_CIRCULATION: 'documents.filters.circulation.inCirculation',
  WITHDRAWN: 'documents.filters.circulation.withdrawn',
  ANY: 'documents.filters.circulation.any',
};

/**
 * Подпись **фишки** — отдельная от подписи варианта в поле, и это не дублирование.
 *
 * В поле рядом стоит его собственная подпись «Обращение», поэтому вариант читается как «только
 * выведенные». Фишка стоит одна среди других фишек, без подписи поля, и «только выведенные» там не
 * отвечает, о чём речь. Одна и та же строка на два места дала бы либо непонятную фишку, либо поле,
 * повторяющее собственную подпись в каждом варианте.
 *
 * `ANY` в карту входит ради полноты `Record`, но фишки не получает: снятая фишка **и есть** `ANY`
 * (см. `removeFilter` в `use-document-filters.hook.ts`), и фишка, показывающая снятое состояние,
 * была бы фишкой, которую нечем снять.
 */
export const DOCUMENT_CIRCULATION_CHIP_LABEL: Readonly<Record<DocumentCirculation, string>> = {
  IN_CIRCULATION: 'documents.filters.chip.circulationInCirculation',
  WITHDRAWN: 'documents.filters.chip.circulationWithdrawn',
  ANY: 'documents.filters.chip.circulationAny',
};

/** Пришло ли из адресной строки то, что реестр умеет спросить: whitelist недоверенного входа. */
export const isDocumentCirculation = (value: unknown): value is DocumentCirculation =>
  typeof value === 'string' && (DOCUMENT_CIRCULATIONS as readonly string[]).includes(value);
